import React, { useMemo } from "react";
import datasets from "../../../../constants/DataSet";
import {
  buildCompanyHeaderTags,
  buildCompanyOverview,
} from "../../utils/companyDetail";
import { buildCompanySourceGroupSummary, parseCompanySourceIdList } from "../../utils/companyEditor";
import { hasAdminEditAudit } from "../../utils/auditHelpers";
import { shouldShowDocStatusFeature } from "../../utils/docStatus";
import useCompanySources from "../../hooks/useCompanySources";
import useRecordEditor from "../../hooks/useRecordEditor";
import CompanyEditor from "./company/CompanyEditor";
import RecordDetailActions from "./RecordDetailActions";
import RecordDetailBody from "./RecordDetailBody";
import RecordDetailHeader from "./RecordDetailHeader";
import RecordEditor from "./editor/RecordEditor";

const RecordDetailCard = ({
  datasetKey = "",
  datasetLabel,
  detailModel,
  displayRecord,
  editableRecord,
  auditMetadata,
  theme,
  loading,
  error,
  onClose,
  onDownload,
  onSave,
  onViewOriginal,
  onViewCurrent,
  onRevert,
  onAddCompanySources,
  onRemoveCompanySources,
  canEdit = false,
  isOriginalView = false,
  isSaving = false,
  isReverting = false,
  saveError = "",
  revertError = "",
  saveLabel = "Save Changes",
  showDownload = true,
  downloadDisabled = false,
  downloadLabel = "Download CSV",
  onOpenLinkedRecord,
}) => {
  const companyRecordId = editableRecord?._record_id || displayRecord?._record_id || "";
  const showDocStatusControl = canEdit && shouldShowDocStatusFeature(datasetKey);
  const {
    isEditing,
    formState,
    formError,
    editableEntries,
    companyEditableEntries,
    companyAliases,
    docStatus,
    changeField,
    changeDocStatus,
    addArrayItem,
    removeArrayItem,
    changeArrayItem,
    addObjectField,
    removeObjectField,
    changeObjectField,
    addCompanyAlias,
    removeCompanyAlias,
    changeCompanyAlias,
    selectedArrayItemByKey,
    selectedObjectFieldByKey,
    selectArrayItem,
    selectObjectField,
    startEditing,
    cancelEditing,
    save,
  } = useRecordEditor({
    datasetKey,
    editableRecord,
    onSave,
    saveError,
    enableDocStatusWorkflow: showDocStatusControl,
  });

  const {
    isSourceWindowOpen,
    sourceWindowDataset,
    sourceWindowInput,
    sourceWindowError,
    sourceInlineInputByDataset,
    sourceInlineErrorByDataset,
    openSourceWindow,
    closeSourceWindow,
    setSourceWindowDataset,
    setSourceWindowInput,
    setSourceInlineErrorByDataset,
    handleSourceInlineInputChange,
    handleRemoveCompanySourceIds,
    clearSourceInlineInput,
    submitCompanySourceIds,
    handleSubmitCompanySources,
  } = useCompanySources({
    onAddCompanySources,
    onRemoveCompanySources,
    companyRecordId,
  });

  const isCompanyDetail = datasetKey === "company" && Boolean(detailModel);
  const isCompanyDetailLoading = datasetKey === "company" && loading;
  const shouldShowBlockingLoader = isCompanyDetailLoading || (loading && !detailModel);
  const hasEditAudit = hasAdminEditAudit(auditMetadata);
  const companyDisplayRecord = displayRecord || editableRecord;
  const companyOverview = isCompanyDetail ? buildCompanyOverview(companyDisplayRecord) : null;
  const companyHeaderTags = isCompanyDetail ? buildCompanyHeaderTags(companyDisplayRecord) : [];
  const companySourceGroups = isCompanyDetail
    ? buildCompanySourceGroupSummary(companyDisplayRecord)
    : [];
  const sourceDatasetOptions = useMemo(
    () => datasets.filter((dataset) => dataset.id !== "company"),
    []
  );

  const handleSubmitInlineSources = async (dataset) => {
    const inlineSourceIds = parseCompanySourceIdList(sourceInlineInputByDataset[dataset] || "");
    if (!inlineSourceIds.length) {
      setSourceInlineErrorByDataset((current) => ({
        ...current,
        [dataset]: "Add at least one source ID.",
      }));
      return;
    }

    setSourceInlineErrorByDataset((current) => ({
      ...current,
      [dataset]: "",
    }));

    try {
      await submitCompanySourceIds({
        dataset,
        sourceIds: inlineSourceIds,
        clearInput: true,
      });
    } catch (sourceError) {
      setSourceInlineErrorByDataset((current) => ({
        ...current,
        [dataset]: sourceError?.message || "Unable to attach the selected sources.",
      }));
    }
  };

  const sourceWindowState = {
    isOpen: isSourceWindowOpen,
    sourceWindowDataset,
    sourceWindowInput,
    sourceWindowError,
    onClose: closeSourceWindow,
    onDatasetChange: setSourceWindowDataset,
    onSourceInputChange: setSourceWindowInput,
    onSubmit: handleSubmitCompanySources,
  };

  const handleRevert = async () => {
    if (!onRevert) {
      return;
    }

    await onRevert();
    cancelEditing();
  };

  const editorContent = isEditing ? (
    datasetKey === "company" ? (
      <CompanyEditor
        formError={formError}
        companyAliases={companyAliases}
        onAddCompanyAlias={addCompanyAlias}
        onRemoveCompanyAlias={removeCompanyAlias}
        onCompanyAliasChange={changeCompanyAlias}
        companyEditableEntries={companyEditableEntries}
        formState={formState}
        onChange={changeField}
        onAddArrayItem={addArrayItem}
        onRemoveArrayItem={removeArrayItem}
        onArrayItemChange={changeArrayItem}
        onAddObjectField={addObjectField}
        onRemoveObjectField={removeObjectField}
        onObjectFieldChange={changeObjectField}
        selectedArrayItemByKey={selectedArrayItemByKey}
        selectedObjectFieldByKey={selectedObjectFieldByKey}
        onSelectArrayItem={selectArrayItem}
        onSelectObjectField={selectObjectField}
        companySourceGroups={companySourceGroups}
        sourceInlineInputByDataset={sourceInlineInputByDataset}
        sourceInlineErrorByDataset={sourceInlineErrorByDataset}
        onSourceInlineInputChange={handleSourceInlineInputChange}
        onClearSourceInlineInput={clearSourceInlineInput}
        onSubmitInlineSources={handleSubmitInlineSources}
        onRemoveSourceIds={handleRemoveCompanySourceIds}
        isSaving={isSaving}
        sourceWindowState={sourceWindowState}
        sourceDatasetOptions={sourceDatasetOptions}
        onOpenSourceWindow={openSourceWindow}
      />
    ) : (
      <RecordEditor
        editableEntries={editableEntries}
        formState={formState}
        formError={formError}
        onChange={changeField}
        onAddArrayItem={addArrayItem}
        onRemoveArrayItem={removeArrayItem}
        onArrayItemChange={changeArrayItem}
        onAddObjectField={addObjectField}
        onRemoveObjectField={removeObjectField}
        onObjectFieldChange={changeObjectField}
        selectedArrayItemByKey={selectedArrayItemByKey}
        selectedObjectFieldByKey={selectedObjectFieldByKey}
        onSelectArrayItem={selectArrayItem}
        onSelectObjectField={selectObjectField}
      />
    )
  ) : null;

  return (
    <section
      className={`relative overflow-visible rounded-[1.35rem] border bg-[#121924]/95 shadow-[0_24px_60px_rgba(2,6,23,0.55)] ${theme.detailBorder}`}
    >
      <div
        className={`pointer-events-none absolute inset-x-0 top-0 h-28 rounded-t-[1.35rem] bg-gradient-to-b ${theme.detailGlow} via-transparent to-transparent`}
      />

      <div className="relative border-b border-slate-800/90">
        <RecordDetailActions
          canEdit={canEdit}
          detailModel={detailModel}
          loading={loading}
          isSaving={isSaving}
          isReverting={isReverting}
          saveLabel={saveLabel}
          isEditing={isEditing}
          hasEditAudit={hasEditAudit}
          startEditing={startEditing}
          cancelEditing={cancelEditing}
          save={save}
          showDocStatusControl={showDocStatusControl}
          docStatus={docStatus}
          onDocStatusChange={changeDocStatus}
          revertError={revertError}
          onRevert={handleRevert}
          isOriginalView={isOriginalView}
          onViewOriginal={onViewOriginal}
          onViewCurrent={onViewCurrent}
          onDownload={onDownload}
          showDownload={showDownload}
          downloadDisabled={downloadDisabled}
          downloadLabel={downloadLabel}
          onClose={onClose}
        />

        {shouldShowBlockingLoader ? (
          <div className="px-6 py-6">
            <div className="rounded-2xl border border-dashed border-slate-700 bg-slate-950/50 px-4 py-10 text-center text-sm text-slate-400">
              Loading record details...
            </div>
          </div>
        ) : detailModel ? (
          <RecordDetailHeader
            datasetLabel={datasetLabel}
            detailModel={detailModel}
            theme={theme}
            isOriginalView={isOriginalView}
            isCompanyDetail={isCompanyDetail}
            companyOverview={companyOverview}
            companyHeaderTags={companyHeaderTags}
          />
        ) : (
          <div className="px-6 py-6">
            <div className="rounded-2xl border border-dashed border-slate-700 bg-slate-950/50 px-4 py-10 text-center text-sm text-slate-400">
              Select a record to open the detail card.
            </div>
          </div>
        )}
      </div>

      {error ? (
        <div className="px-6 py-6">
          <div className="rounded-2xl border border-rose-500/20 bg-rose-500/10 px-4 py-4 text-sm text-rose-100">
            {error}
          </div>
        </div>
      ) : detailModel ? (
        isEditing ? (
          <div className="relative px-6 py-6">
            {revertError ? (
              <div className="mb-4 rounded-2xl border border-rose-500/20 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
                {revertError}
              </div>
            ) : null}
            {editorContent}
          </div>
        ) : (
          <RecordDetailBody
            detailModel={detailModel}
            theme={theme}
            isCompanyDetail={isCompanyDetail}
            companyDisplayRecord={companyDisplayRecord}
            auditMetadata={auditMetadata}
            canEdit={canEdit}
            onOpenLinkedRecord={onOpenLinkedRecord}
          />
        )
      ) : null}
    </section>
  );
};

export default RecordDetailCard;
